import { spreadMap } from '@ardoq/rxbeach';
import { permissionsOperations } from '@ardoq/access-control';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { buildConditionalFormattingCommand } from 'aiControlledCommands/viewpoint/conditionalFormatting/command';
import { buildUiNavigationCommand } from 'aiControlledCommands/viewpoint/navigation/command';
import { buildDiagramCommand } from 'aiControlledCommands/viewpoint/diagram/command';
import { buildInteractiveBlocksLayoutCommand } from 'aiControlledCommands/viewpoint/blocks/interactiveBlocksCommand';
import { buildAssistantBlocksLayoutCommand } from 'aiControlledCommands/viewpoint/blocks/assistantBlocksCommand';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { parseArrangements } from 'tabview/assistantBlocks/arrangementRecipe';
import {
  buildTraversalContextCommand,
  hasTraversalDataset,
} from 'aiControlledCommands/viewpoint/traversalContext/command';
import type { ViewpointsById } from 'aiControlledCommands/viewpoint/traversalDatasets';
import {
  buildRequiredPathsCommand,
  hasRequirableDataset,
} from 'aiControlledCommands/viewpoint/requiredPaths/command';
import { loadedState$ } from 'loadedState/loadedState$';
import type { LoadedStatesState } from 'loadedState/loadedStateOperations';
import { traversalState$ } from 'streams/traversals$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { sidebarPanel$ } from 'canvas/sidebarPanel/sidebarPanel$';
import type { SidebarPanelState } from 'canvas/sidebarPanel/types';
import { ViewIds } from '@ardoq/api-types';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { featureContext$ } from 'streams/featureContext$';
import {
  activeView$,
  type ActiveViews,
} from 'streams/views/mainContent/activeView$';
import { isViewpointModeView } from 'views/environmentals/viewSettings/utils';
import {
  featureOperations,
  Features,
  type FeatureContext,
} from '@ardoq/features';
import type {
  FormattingAndLabelRules,
  LoadedGraphWithViewpointMode,
} from '@ardoq/data-model';
import { buildLabelFormattingCommand } from 'aiControlledCommands/viewpoint/labelFormatting/command';
import { ViewpointModeViewSettingsWithChanges } from 'views/environmentals/viewSettings/types';
import { buildChangeViewCommand } from 'aiControlledCommands/viewpoint/changeView/command';
import { buildChangeViewSettingsCommand } from 'aiControlledCommands/viewpoint/viewSettings/command';
import { viewpointModeSettingsForAllViews$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import {
  currentViewProperties$,
  CurrentViewPropertiesState,
} from 'aiControlledCommands/viewpoint/viewSettings/currentViewProperties$';
import { type ModeViews, viewpointModeViews$ } from 'views/modeViews$';
import type { ViewpointModeViewKey } from 'views/environmentals/viewSettings/types';
import { assistantDiagram$ } from 'tabview/assistantDiagram/assistantDiagram$';
import type { AiControlledCommand } from 'aiControlledCommands/types';

export type AppContextForUser = {
  loadedGraph: LoadedGraphWithViewpointMode;
  featureContext: FeatureContext;
  activeView: ActiveViews;
  isOrgContributor: boolean;
};

const isOrgContributor$ = currentUserPermissionContext$.pipe(
  map(permissionContext =>
    permissionsOperations.hasOrgContributorRole(permissionContext)
  ),
  distinctUntilChanged()
);

const appContextForUser$ = combineLatest({
  loadedGraph: loadedGraph$,
  featureContext: featureContext$,
  activeView: activeView$,
  isOrgContributor: isOrgContributor$,
});

const isAiViewConfigContext = (
  loadedGraph: LoadedGraphWithViewpointMode,
  { mainViewId }: ActiveViews
): boolean => loadedGraph.isViewpointMode && isViewpointModeView(mainViewId);

const toConditionalFormattingCommands = (
  { loadedGraph, activeView, isOrgContributor }: AppContextForUser,
  formattingAndLabelRules: FormattingAndLabelRules
): AiControlledCommand[] => {
  if (isOrgContributor || !isAiViewConfigContext(loadedGraph, activeView)) {
    return [];
  }
  return [
    buildConditionalFormattingCommand(loadedGraph, formattingAndLabelRules),
  ];
};

const toLabelFormattingCommands = (
  { loadedGraph, activeView, isOrgContributor }: AppContextForUser,
  formattingAndLabelRules: FormattingAndLabelRules,
  locale: Parameters<typeof buildLabelFormattingCommand>[3]
): AiControlledCommand[] => {
  if (isOrgContributor || !isAiViewConfigContext(loadedGraph, activeView)) {
    return [];
  }
  return [
    buildLabelFormattingCommand(
      loadedGraph,
      formattingAndLabelRules,
      activeView.mainViewId,
      locale
    ),
  ];
};

const toUiNavigationCommands = (
  {
    loadedGraph,
    featureContext,
    activeView,
    isOrgContributor,
  }: AppContextForUser,
  { contentId: currentPanel }: SidebarPanelState
): AiControlledCommand[] => {
  // UI navigation opens the canvas sidebar panel, which only exists in the
  // canvas prototype.
  if (
    isOrgContributor ||
    !isAiViewConfigContext(loadedGraph, activeView) ||
    !featureOperations.hasFeature(featureContext, Features.CANVAS_PROTOTYPE)
  ) {
    return [];
  }
  return [buildUiNavigationCommand(currentPanel)];
};

const toDiagramCommands = (
  { loadedGraph, featureContext, activeView }: AppContextForUser,
  { recipe }: typeof assistantDiagram$.state
): AiControlledCommand[] => {
  if (
    activeView.mainViewId !== ViewIds.ASSISTANT_DIAGRAM ||
    !featureOperations.hasFeature(featureContext, Features.ASSISTANT_DIAGRAM)
  ) {
    return [];
  }
  return [buildDiagramCommand(loadedGraph, recipe)];
};

const toChangeViewCommands = (
  { loadedGraph, activeView, isOrgContributor }: AppContextForUser,
  { ids: switchableViewIds }: ModeViews<ViewpointModeViewKey>
): AiControlledCommand[] => {
  if (isOrgContributor || !isAiViewConfigContext(loadedGraph, activeView)) {
    return [];
  }
  const { mainViewId } = activeView;
  return [
    buildChangeViewCommand({
      currentView: mainViewId,
      switchableViewIds,
    }),
  ];
};

const toChangeViewSettingsCommands = (
  { loadedGraph, activeView, isOrgContributor }: AppContextForUser,
  { currentSettings }: ViewpointModeViewSettingsWithChanges,
  currentViewProperties: CurrentViewPropertiesState
): AiControlledCommand[] => {
  if (isOrgContributor || !isAiViewConfigContext(loadedGraph, activeView)) {
    return [];
  }
  const { mainViewId } = activeView;
  return [
    buildChangeViewSettingsCommand({
      currentView: mainViewId,
      settingsByViewId: currentSettings,
      currentViewProperties,
    }),
  ];
};

const conditionalFormattingCommands$ = combineLatest([
  appContextForUser$,
  formattingAndLabelRules$,
]).pipe(spreadMap(toConditionalFormattingCommands));

const labelFormattingCommands$ = combineLatest([
  appContextForUser$,
  formattingAndLabelRules$,
  locale$,
]).pipe(
  map(([appContextForUser, formattingAndLabelRules, locale]) =>
    toLabelFormattingCommands(
      appContextForUser,
      formattingAndLabelRules,
      locale
    )
  )
);

const uiNavigationCommands$ = combineLatest([
  appContextForUser$,
  sidebarPanel$,
]).pipe(spreadMap(toUiNavigationCommands));

const diagramCommands$ = combineLatest([
  appContextForUser$,
  assistantDiagram$,
]).pipe(spreadMap(toDiagramCommands));

const changeViewCommands$ = combineLatest([
  appContextForUser$,
  viewpointModeViews$,
]).pipe(spreadMap(toChangeViewCommands));

const changeViewSettingsCommands$ = combineLatest([
  appContextForUser$,
  viewpointModeSettingsForAllViews$,
  currentViewProperties$,
]).pipe(spreadMap(toChangeViewSettingsCommands));

const groupingRulesInterface$ = groupingRules$.pipe(
  map(({ groupingRules }) =>
    groupingRules.map(groupingRuleInterface.toInterfaceType)
  )
);

export const toBlocksCommands = (
  { loadedGraph, activeView }: AppContextForUser,
  groupingRules: ReturnType<typeof groupingRuleInterface.toInterfaceType>[],
  i18nLocale: I18nLocale,
  locale: Locale,
  { currentSettings }: ViewpointModeViewSettingsWithChanges,
  { ids: enabledViewIds }: ModeViews<ViewpointModeViewKey>
): AiControlledCommand[] => {
  const viewId = activeView.mainViewId;
  if (!enabledViewIds.some(id => id === viewId)) return [];
  const contextArgs = { loadedGraph, groupingRules, i18nLocale, locale };
  if (viewId === ViewIds.BLOCKS) {
    const settings = currentSettings[ViewIds.BLOCKS];
    return [
      buildInteractiveBlocksLayoutCommand({
        ...contextArgs,
        arrangements: parseArrangements(settings?.arrangements),
        storedLayout: settings?.layoutData ?? null,
      }),
    ];
  }
  if (viewId === ViewIds.ASSISTANT_BLOCKS) {
    const settings = currentSettings[ViewIds.ASSISTANT_BLOCKS];
    return [
      buildAssistantBlocksLayoutCommand({
        ...contextArgs,
        title: settings?.title ?? null,
        arrangements: parseArrangements(settings?.arrangements),
      }),
    ];
  }
  return [];
};

const canEditTraversalDatasets = ({
  loadedGraph,
  featureContext,
  activeView,
  isOrgContributor,
}: AppContextForUser): boolean =>
  !isOrgContributor &&
  isAiViewConfigContext(loadedGraph, activeView) &&
  featureOperations.hasFeature(
    featureContext,
    Features.VISUALIZATIONS_TMP_AI_VIEW_CONFIGURATION_EXTENDED
  );

export const toTraversalDatasetCommands = (
  appContextForUser: AppContextForUser,
  { loadedStates }: LoadedStatesState,
  viewpointsById: ViewpointsById
): AiControlledCommand[] => {
  if (!canEditTraversalDatasets(appContextForUser)) {
    return [];
  }
  return [
    ...(hasTraversalDataset(loadedStates)
      ? [buildTraversalContextCommand(loadedStates, viewpointsById)]
      : []),
    ...(hasRequirableDataset(loadedStates)
      ? [buildRequiredPathsCommand(loadedStates, viewpointsById)]
      : []),
  ];
};

const traversalDatasetCommands$ = combineLatest([
  appContextForUser$,
  loadedState$,
  traversalState$,
]).pipe(
  map(([appContextForUser, loadedState, traversalState]) =>
    toTraversalDatasetCommands(
      appContextForUser,
      loadedState,
      traversalState.byId
    )
  )
);

const blocksCommands$ = combineLatest([
  appContextForUser$,
  groupingRulesInterface$,
  i18nLocale$,
  locale$,
  viewpointModeSettingsForAllViews$,
  viewpointModeViews$,
]).pipe(spreadMap(toBlocksCommands));

export const availableCommands$ = combineLatest([
  conditionalFormattingCommands$,
  labelFormattingCommands$,
  uiNavigationCommands$,
  diagramCommands$,
  changeViewCommands$,
  changeViewSettingsCommands$,
  blocksCommands$,
  traversalDatasetCommands$,
]).pipe(map(commands => commands.flat()));
